import * as d3 from 'd3';
import { mean as getAvg, max as getMax, min as getMin, sum as getSum, isEmpty, isNil } from 'lodash-es';
import moment from 'moment-timezone';
import { isForecastDataSource } from '../../enums/DataSources';
import { AggregationType } from '../../resources/measurements.type';
export function getTimeDomain(points) {
    const xDomain = d3.extent(points.map((pt) => pt.date));
    if (xDomain[0] === undefined) {
        throw new Error('xdomain invalid');
    }
    return xDomain;
}
export function getDomains(points) {
    const xDomain = getTimeDomain(points);
    const yDomain = d3.extent(points
        .filter((pt) => pt.value !== null)
        .map((pt) => pt.value));
    if (yDomain[0] === undefined) {
        throw new Error('ydomain invalid');
    }
    return {
        x: xDomain,
        y: yDomain
    };
}
// export function getOrderedValuesFromSets(sets: DataSet[]) {
//   const initialOrderedValue: Record<number, Partial<Record<AllKey, number | null>>> = {}
//   return sets.reduce((acc, s) => {
//     s.data.forEach((dp) => {
//       const timestamp = dp.date.getTime()
//       if (acc[timestamp] == undefined) {
//         acc[timestamp] = { [s.metric]: dp.value }
//       } else {
//         acc[timestamp][s.metric] = dp.value
//       }
//     })
//     return acc
//   }, initialOrderedValue)
// }
export function makeXScale({ domain, marginLeft, marginRight, width, paddingStart = 0, paddingEnd = 0 }) {
    const range = [marginLeft, width - marginRight];
    const scale = d3.scaleUtc(domain, range);
    scale.domain([
        new Date(domain[0].getTime() - (scale.invert(range[0] + paddingStart).getTime() - domain[0].getTime())),
        new Date(domain[1].getTime() + (domain[1].getTime() - scale.invert(range[1] - paddingEnd).getTime()))
    ]);
    return scale;
}
export function makeYScale({ domain, marginTop, marginBottom, height, paddingStart = 0, paddingEnd = 0 }) {
    // Pixel Range
    const range = [height - marginBottom, marginTop];
    const scale = d3.scaleLinear(domain, range);
    scale.domain([
        domain[0] - (scale.invert(range[0] - paddingStart) - domain[0]),
        domain[1] + (domain[1] - scale.invert(range[1] + paddingEnd))
    ]);
    return scale;
}
export function toDataPoint(data) {
    return { date: data.x, value: data.y };
}
const createDailyInterval = (timezone) => {
    return d3.timeInterval((date) => date.setTime(moment(date).tz(timezone).startOf('day').toDate().getTime()), // floor
    (date, step) => date.setTime(moment(date).tz(timezone).add(step, 'day').toDate().getTime()), // offset
    (start, end) => moment(end).tz(timezone).diff(moment(start).tz(timezone), 'day'), // count
    (date) => date.getDate() - 1 // field
    );
};
const createMonthlyInterval = (timezone) => {
    return d3.timeInterval((date) => date.setTime(moment(date).tz(timezone).startOf('month').toDate().getTime()), // floor
    (date, step) => date.setTime(moment(date).tz(timezone).add(step, 'month').toDate().getTime()), // offset
    (start, end) => moment(end).tz(timezone).diff(moment(start).tz(timezone), 'month'), // count
    (date) => date.getMonth() // field
    );
};
/**
 * This function generate a time interval based on local time instead of UTC one which is the default with d3
 * Mostly used to generate ticks on the time axis of charts
 * @returns d3.timeInterval
 */
export function getTimeInterval(period, timezone, isFourHourTimeStep = false) {
    let timeInterval;
    const tzAwareTimeDay = createDailyInterval(timezone);
    const tzAwareTimeMonth = createMonthlyInterval(timezone);
    const daysInPeriod = moment(period[1]).tz(timezone).diff(moment(period[0]).tz(timezone), 'day');
    const monthsInPeriod = moment(period[1])
        .tz(timezone)
        .diff(moment(period[0]).tz(timezone), 'month');
    if (daysInPeriod <= 1) {
        //make an hour timeInterval
        timeInterval = d3
            .timeInterval((date) => date.setTime(moment(date).tz(timezone).startOf('hour').toDate().getTime()), // floor
        (date, step) => date.setTime(moment(date).tz(timezone).add(step, 'hour').toDate().getTime()), // offset
        (start, end) => moment(end).tz(timezone).diff(moment(start).tz(timezone), 'hour', true), // count
        (date) => date.getHours() // field
        )
            .every(isFourHourTimeStep ? 4 : 1);
    }
    else if (daysInPeriod >= 2 && monthsInPeriod < 1) {
        // make a day time interval
        timeInterval = tzAwareTimeDay;
    }
    else if (monthsInPeriod >= 1 && monthsInPeriod < 3) {
        //make a week time interval
        timeInterval = d3.timeInterval((date) => date.setTime(moment(date).tz(timezone).startOf('week').toDate().getTime()), // floor
        (date, step) => date.setTime(moment(date).tz(timezone).add(step, 'week').toDate().getTime()), // offset
        (start, end) => moment(end).tz(timezone).diff(moment(start).tz(timezone), 'week'), // count
        (date) => date.getDate() % 7 // field
        );
    }
    else {
        //make a month time interval
        timeInterval = tzAwareTimeMonth;
    }
    return timeInterval;
}
/**
 *
 * @param timeStep
 * @returns a moment time format {@link https://momentjs.com/docs/#/parsing/string-format/}
 */
export function getTimeFormatForTicks(period, timezone) {
    let timeFormat;
    const daysInPeriod = moment(period[1]).tz(timezone).diff(moment(period[0]).tz(timezone), 'day');
    const monthsInPeriod = moment(period[1])
        .tz(timezone)
        .diff(moment(period[0]).tz(timezone), 'month');
    if (daysInPeriod <= 0) {
        timeFormat = 'HH[h]';
    }
    else if (daysInPeriod === 1) {
        timeFormat = 'DD/MM HH[h]';
    }
    else if (daysInPeriod > 1 && monthsInPeriod < 3) {
        timeFormat = 'DD/MM';
    }
    else {
        //make a month time interval
        timeFormat = 'MMM YY';
    }
    return timeFormat;
}
/**
 * this function is used to find the closest number to a goal number
 * @param vals an array of number which should be sorted asc
 * @param goal the number we are looking
 * @returns the closest number among vals in the form of a recod of value and idx
 */
export function getClosest(vals, goal) {
    'worklet';
    //previous line used by react native reanimated to use this function on ui thread
    return vals.reduce((prev, curr, currIdx) => Math.abs(curr - goal) < Math.abs(prev.val - goal) ? { val: curr, idx: currIdx } : prev, { val: vals[0], idx: 0 });
}
/**
 * Returns an object with the minimum, maximum, sum & average of the given dataset
 * Might be replace by meta data from back end.
 */
function getAggregationsFromDataPoints(points) {
    const values = !isNil(points)
        ? points.reduce((acc, datum) => {
            if (!isNil(datum.value))
                acc.push(datum.value);
            return acc;
        }, [])
        : undefined;
    let min = !isEmpty(values) ? getMin(values) : null;
    let max = !isEmpty(values) ? getMax(values) : null;
    let sum = !isEmpty(values) ? getSum(values) : null;
    let avg = !isEmpty(values) ? getAvg(values) : null;
    //force null
    if (min === undefined) {
        min = null;
    }
    if (max === undefined) {
        max = null;
    }
    if (sum === undefined) {
        sum = null;
    }
    if (avg === undefined) {
        avg = null;
    }
    return {
        min,
        max,
        sum,
        avg,
        count: null
    };
}
/**
 * Extract aggregations by cleaning the data first and then by reusing {@link getAggregationsFromDataPoints}
 * @param data non cleaned data points
 * @returns an aggregation record
 */
export function extractAggregations(data, type) {
    // Filtering out forecast values to compute aggregates
    const rawAggregates = getAggregationsFromDataPoints(data.filter((point) => !isNil(point.source) && !isForecastDataSource(point.source)));
    const initialInfos = {
        min: null,
        max: null,
        avg: null,
        sum: type === AggregationType.sum ? 0 : null,
        count: null
    };
    const aggregates = Object.entries(rawAggregates).reduce((acc, [key, value]) => ({
        ...acc,
        [key]: value
    }), initialInfos);
    return aggregates;
}
/**
 * Goes through the given data points and counts the number of values above the given threshold
 * @returns a number of values above the threshold
 */
export function getValuesCountOverThreshold({ dataPoints, threshold, isCumulativeSum }) {
    return dataPoints.reduce((amount, currentDataPoint, idx, allDataPoints) => {
        const currentValue = currentDataPoint.value;
        if (!isNil(currentValue)) {
            if (isCumulativeSum) {
                const nextValue = allDataPoints[idx + 1]?.value;
                const delta = !isNil(nextValue) ? nextValue - currentValue : null;
                // for cumulative sums we have to compare the delta to the thresholds
                if (!isNil(delta) && delta > threshold) {
                    amount = amount + 1;
                }
            }
            else if (currentValue > threshold) {
                amount = amount + 1;
            }
        }
        return amount;
    }, 0);
}
/**
 * Extracts data points in between null values - for points that cannot be traced (to form a line) because they have no direct connections
 * @param data to be checks for standalone points
 * @returns an array of DataPoint between nulls
 */
export function extractPointsBetweenNulls(data) {
    const results = [];
    const lastIndex = data.length - 1;
    data.forEach((dp, index) => {
        if (index > 0 &&
            index < lastIndex &&
            data.at(index - 1)?.value === null &&
            data.at(index + 1)?.value === null &&
            dp.value !== null) {
            results.push(dp);
        } // Check if it's the first object and the next one is null
        else if (index === 0 && data.at(index + 1)?.value === null && dp.value !== null) {
            results.push(dp);
        }
        // Check if it's the last object and the one before is null
        else if (index === lastIndex && data.at(index - 1)?.value === null && dp.value !== null) {
            results.push(dp);
        }
    });
    return results;
}
